import React, { useCallback, useState, useEffect, useRef } from "react";
import { Button, Flex } from "orcs-design-system";
import PropTypes from "prop-types";
import { ACTION_TYPES } from "src/components/OrgChart/context/actionTypes";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import { useChangelogFeatureEnabled } from "src/components/OrgChart/odDemo/featureFlags";

/**
 * Button component that triggers the changelog view for org chart diff data.
 * Automatically loads diff data if not already loaded when clicked.
 * Only shows when dates are set and showChanges is enabled.
 * @param {Object} props - Component props.
 * @param {Object} props.state - Org chart state containing viewOptions, teams, and members.
 * @param {Function} props.dispatch - Redux-style dispatch function for updating state.
 * @returns {React.ReactElement|null} The changelog button or null if conditions aren't met.
 */
const ChangelogButton = ({ state, dispatch }) => {
  const isChangelogEnabled = useChangelogFeatureEnabled();
  const { showChanges, fromDate, toDate } = state?.viewOptions || {};
  const { teams = {}, members = {} } = state || {};
  const { loadTeamDataById, teamId } = useOrgChartContext();
  const [isLoading, setIsLoading] = useState(false);
  const shouldOpenOnLoadRef = useRef(false);

  const hasDiffData =
    showChanges &&
    (Object.values(teams).some((team) => team?.diff !== undefined) ||
      Object.values(members).some((member) => member?.diff !== undefined));

  const canShowButton = showChanges && fromDate && toDate;

  useEffect(() => {
    if (hasDiffData && isLoading && shouldOpenOnLoadRef.current) {
      setIsLoading(false);
      shouldOpenOnLoadRef.current = false;
      dispatch({
        type: ACTION_TYPES.TOGGLE_CHANGELOG_VIEW,
      });
    }
  }, [hasDiffData, isLoading, dispatch]);

  const handleClick = useCallback(async () => {
    if (!hasDiffData && loadTeamDataById && teamId) {
      setIsLoading(true);
      shouldOpenOnLoadRef.current = true;
      try {
        await loadTeamDataById(teamId, teamId);
      } catch (error) {
        setIsLoading(false);
        shouldOpenOnLoadRef.current = false;
      }
    } else if (hasDiffData) {
      dispatch({
        type: ACTION_TYPES.TOGGLE_CHANGELOG_VIEW,
      });
    }
  }, [hasDiffData, loadTeamDataById, teamId, dispatch]);

  if (!isChangelogEnabled) {
    return null;
  }

  if (!canShowButton) {
    return null;
  }

  return (
    <Flex mt="s">
      <Button
        onClick={handleClick}
        size="small"
        variant="secondary"
        disabled={isLoading}
      >
        {isLoading ? "Loading..." : "View Changelog"}
      </Button>
    </Flex>
  );
};

ChangelogButton.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default ChangelogButton;
